import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '../utils/toast';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SelfConfigurationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Always enabled items
  const [dashboardItems, setDashboardItems] = useState([
    'rewards_recognition',
    'activity',
    'feedback'
  ]);
  
  const [orgSetupType, setOrgSetupType] = useState('default');
  const [feedbackServiceEnabled, setFeedbackServiceEnabled] = useState(true);
  
  const optionalItems = [
    { id: 'audit_compliance', label: 'Audit & Compliance' },
    { id: 'patient_care', label: 'Patient Care' },
    { id: 'staff_performance', label: 'Staff Performance' },
    { id: 'cost_effectiveness', label: 'Cost-Effectiveness' }
  ];
  
  const handleItemToggle = (itemId) => {
    if (dashboardItems.includes(itemId)) {
      setDashboardItems(dashboardItems.filter(id => id !== itemId));
    } else {
      setDashboardItems([...dashboardItems, itemId]);
    }
  };
  
  const handleReset = () => {
    setDashboardItems(['rewards_recognition', 'activity', 'feedback']);
    setOrgSetupType('default');
    setFeedbackServiceEnabled(true);
    toast.info('Selections reset to defaults');
  };
  
  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/hospital/configuration/save`,
        {
          dashboard_items: dashboardItems,
          org_setup_type: orgSetupType,
          feedback_service_enabled: feedbackServiceEnabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Your dashboard has been successfully configured.');
      setTimeout(() => {
        navigate('/hospital/staff-management');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Configuration failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#0056B3] to-[#00AEEF] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">E</span>
            </div>
            <h1 className="text-[28px] font-bold mb-2" style={{ color: '#0056B3' }}>
              Welcome to Eptral.Hospital!
            </h1>
            <p className="text-gray-600">Let's configure your hospital dashboard</p>
          </div>
        
        <Card className="p-8">
          {/* Dashboard Items */}
          <div className="mb-8">
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>
              Select Dashboard Items
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              These items are enabled by default and will always appear on your dashboard.
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Checkbox checked disabled />
                <Label className="text-gray-700">Rewards & Recognition (Always Enabled)</Label>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Checkbox checked disabled />
                <Label className="text-gray-700">Activity (Always Enabled)</Label>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Checkbox checked disabled />
                <Label className="text-gray-700">Feedback (Always Enabled)</Label>
              </div>
            </div>
            
            <p className="text-sm font-medium text-gray-700 mb-3">Optional Dashboard Items:</p>
            <div className="space-y-3">
              {optionalItems.map(item => (
                <div key={item.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                  <Checkbox
                    checked={dashboardItems.includes(item.id)}
                    onCheckedChange={() => handleItemToggle(item.id)}
                  />
                  <Label className="cursor-pointer">{item.label}</Label>
                </div>
              ))}
            </div>
          </div>
          
          {/* Feedback Service */}
          <div className="mb-8">
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>
              Feedback Service
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Enable or disable post-training feedback collection from staff members.
            </p>
            
            <div className="flex items-start space-x-3 p-4 border rounded-lg">
              <Checkbox
                checked={feedbackServiceEnabled}
                onCheckedChange={setFeedbackServiceEnabled}
                id="feedback-service"
              />
              <div>
                <Label htmlFor="feedback-service" className="cursor-pointer font-semibold">
                  Enable Feedback Popup After Topic Completion
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  When enabled, staff will see a feedback popup immediately after completing a training chapter to evaluate learning quality and engagement.
                </p>
              </div>
            </div>
          </div>
          
          {/* Organizational Setup */}
          <div className="mb-8">
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>
              Organizational Setup
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Choose how you want to structure your organization:
            </p>
            
            <div className="space-y-3">
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  orgSetupType === 'default'
                    ? 'border-[#0056B3] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setOrgSetupType('default')}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    orgSetupType === 'default' ? 'border-[#0056B3]' : 'border-gray-300'
                  }`}>
                    {orgSetupType === 'default' && (
                      <div className="w-3 h-3 rounded-full bg-[#0056B3]"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">Use Pre-grouped Default Job Roles and Topics</p>
                    <p className="text-sm text-gray-600">Recommended for quick setup with option to edit later</p>
                  </div>
                </div>
              </div>
              
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  orgSetupType === 'custom'
                    ? 'border-[#0056B3] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setOrgSetupType('custom')}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    orgSetupType === 'custom' ? 'border-[#0056B3]' : 'border-gray-300'
                  }`}>
                    {orgSetupType === 'custom' && (
                      <div className="w-3 h-3 rounded-full bg-[#0056B3]"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">Create Custom Job Roles and Map Topics</p>
                    <p className="text-sm text-gray-600">Build your structure from scratch</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex space-x-4">
            <Button
              onClick={handleReset}
              variant="outline"
              className="flex-1"
            >
              Reset Selections
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-[#0056B3] hover:bg-[#0056B3]/90"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save & Continue'
              )}
            </Button>
          </div>
        </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SelfConfigurationPage;